import React, { useEffect, useState } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useCreateTeacher } from '../../hooks/teachers/useCreateTeacher';
import { useUpdateTeacher } from '../../hooks/teachers/useUpdateTeacher';
import { useGetEmployees } from '../../hooks/employees/useGetEmployees';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditTeacher = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();

    const { createTeacher, isPending: isCreating } = useCreateTeacher();
    const { editTeacher, isUpdate } = useUpdateTeacher();
    const { isLoading: empsLoading, employees } = useGetEmployees(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        employee_id: '',
        memorized_parts_count: 0,
        has_ijazah: false,
        ijazah_notes: '',
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            employee_id: currentData.employee_id || '',
            memorized_parts_count: currentData.memorized_parts_count ?? 0,
            has_ijazah: currentData.has_ijazah ?? false,
            ijazah_notes: currentData.ijazah_notes || '',
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') { setForm((p) => ({ ...p, [name]: checked })); return; }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const parts = Number(form.memorized_parts_count);
        if (isNaN(parts) || parts < 0 || parts > 30) {
            enqueueSnackbar(isArabic ? 'عدد الأجزاء يجب أن يكون بين 0 و 30' : 'Parts count must be between 0 and 30', { variant: 'error' });
            return;
        }
        const payload = {
            employee_id: Number(form.employee_id),
            memorized_parts_count: parts,
            has_ijazah: !!form.has_ijazah,
            ijazah_notes: form.ijazah_notes || null,
            is_active: !!form.is_active,
        };
        if (isEdit && currentData?.id) { editTeacher({ req: payload, id: currentData.id }); }
        else { createTeacher(payload); }
    };

    if (empsLoading) return <LoadingPage />;

    const empOpts = (employees || []).map((e) => ({ id: e.id, name: e.full_name }));

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput label={isArabic ? 'الموظف' : 'Employee'} type="select" name="employee_id"
                            value={form.employee_id}
                            onChange={(e) => setForm((p) => ({ ...p, employee_id: e.target.value }))}
                            options={empOpts} disabled={isEdit} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput label={isArabic ? 'عدد الأجزاء المحفوظة (0-30)' : 'Memorized Parts (0-30)'}
                            placeholder="0" type="number" name="memorized_parts_count"
                            value={form.memorized_parts_count} onChange={handleChange} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1 }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.has_ijazah} name="has_ijazah" onChange={handleChange} size="small" color="success" />}
                                label={isArabic ? 'لديه إجازة' : 'Has Ijazah'} />
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'} />
                        </Box>
                    </Grid>

                    {form.has_ijazah && (
                        <Grid item xs={12}>
                            <FormInput label={isArabic ? 'ملاحظات الإجازة' : 'Ijazah Notes'}
                                placeholder={isArabic ? 'تفاصيل الإجازة...' : 'Ijazah details...'}
                                type="textarea" name="ijazah_notes" value={form.ijazah_notes}
                                onChange={handleChange} required={false} />
                        </Grid>
                    )}
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditTeacher;
